/**
 * @file Solr facet UI enhancements for TYPO3 13 with ext:solr.
 */

/**
 * A single parsed Solr filter, e.g. "StudyLevel:140".
 *
 * @typedef {Object} ParsedFilter
 * @property {string} name  Facet name (text before the first colon), e.g. "StudyLevel".
 * @property {string} value Facet value (text after the first colon), e.g. "140".
 */

/**
 * Selector for the container whose content is replaced by the AJAX response.
 * @type {string}
 */
const RESULTS_SELECTOR = '.ucphcontentdocumentlisting_documentlisting';

/**
 * TYPO3 page type (typeNum) that returns only the result markup.
 * It is appended as `&type=...` to the AJAX request only, never to the
 * URL shown in the browser.
 * @type {number}
 */
const AJAX_PAGE_TYPE = 7383;

/**
 * Counter used to identify the most recent AJAX request, so that slow,
 * outdated responses can be ignored when the user clicks quickly.
 * @type {number}
 */
let requestCounter = 0;

// Run once when the initial HTML is ready.
document.addEventListener('DOMContentLoaded', () => {
    initFacetUI();
    initializeAjaxifiedSolr();
});

/**
 * Updates all facet-related UI based on the current browser URL.
 *
 * Safe to call repeatedly. It is called on page load and again after every
 * successful AJAX update.
 *
 * @function initFacetUI
 * @returns {void}
 */
function initFacetUI() {
    const activeFilters = getActiveFiltersFromUrl();

    highlightActiveFacets(activeFilters);
    updateDropdownButtonText();
    scrollToResults();
}

/**
 * Normalizes strings for safe comparison between URL values and DOM values.
 *
 * Handles:
 * - Case differences
 * - Leading/trailing whitespace
 * - "+" used as space in URL encoding
 * - Unicode normalization (e.g. Danish characters æ, ø, å)
 *
 * @function normalize
 * @param {string} [str] The string to normalize. Falsy values become "".
 * @returns {string} The normalized string.
 */
function normalize(str) {
    return (str || '')
        .toLowerCase()
        .trim()
        .replace(/\+/g, ' ')
        .normalize('NFC');
}

/**
 * Registers a single, delegated click handler for all Solr AJAX links.
 *
 * Event delegation is essential here: facet links are replaced when new HTML
 * is inserted, so listeners bound directly to the links would be lost after
 * the first AJAX update. A listener on `document` keeps working for links that
 * are added later.
 *
 * If the results container cannot be found, the link is followed normally.
 *
 * @function initializeAjaxifiedSolr
 * @returns {void}
 */
function initializeAjaxifiedSolr() {
    document.addEventListener('click', (event) => {
        // The click may happen on a child element (icon, span) inside the link.
        const link = event.target.closest('a.solr-ajaxified');
        if (!link) {
            return;
        }

        event.preventDefault();

        const url = new URL(link.href, window.location.origin);
        const container = document.querySelector(RESULTS_SELECTOR);

        if (!container) {
            // Graceful fallback: normal navigation.
            window.location.href = link.href;
            return;
        }

        fetchResults(url, container);
    });
}

/**
 * Fetches the filtered results, injects them into the container and
 * refreshes the facet UI.
 *
 * Order matters: the browser URL is updated BEFORE `initFacetUI()` runs,
 * because the UI reads the active filters from `window.location`.
 *
 * On any error the user is sent to the plain (non-AJAX) URL as a fallback.
 *
 * @async
 * @function fetchResults
 * @param {URL} url              The facet URL (without the AJAX page type).
 * @param {HTMLElement} container Element whose innerHTML is replaced.
 * @returns {Promise<void>}
 */
async function fetchResults(url, container) {
    const currentRequest = ++requestCounter;

    // Build a separate URL for the request so the AJAX page type
    // never ends up in the address bar.
    const requestUrl = new URL(url.href);
    requestUrl.searchParams.append('type', String(AJAX_PAGE_TYPE));

    try {
        const response = await fetch(requestUrl.href);
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        const html = await response.text();

        // A newer request has been started in the meantime: drop this response.
        if (currentRequest !== requestCounter) {
            return;
        }

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incoming = doc.querySelector('#' + container.id);

        container.innerHTML = incoming ? incoming.innerHTML : html;

        // Update the URL first, then re-read it in initFacetUI().
        history.replaceState({}, '', url.href);

        initFacetUI();
    } catch (error) {
        console.error('Solr AJAX failed, falling back to full page load', error);
        window.location.href = url.href;
    }
}

/**
 * Extracts the active Solr filters from the current URL.
 *
 * Example URL:
 *   ?tx_solr%5Bfilter%5D%5B0%5D=Organization%3A%2F27%2F
 *
 * Returns:
 *   ["Organization:/27/"]
 *
 * Note: `URLSearchParams` already decodes percent-encoding (%5B, %3A, %2F),
 * so calling `decodeURIComponent()` again is unnecessary and can throw on
 * values that contain a literal "%".
 *
 * @function getActiveFiltersFromUrl
 * @returns {string[]} Raw filter strings in the form "Name:Value".
 */
function getActiveFiltersFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const filters = [];

    params.forEach((value, key) => {
        // Matches tx_solr[filter][0], tx_solr[filter][1], ...
        if (key.startsWith('tx_solr[filter]')) {
            filters.push(value);
        }
    });

    return filters;
}

/**
 * Splits a raw filter string into facet name and value.
 *
 * Only the FIRST colon is used as separator, because values may contain
 * colons themselves (e.g. "Organization:/27/" or "Date:2024-01-01T10:00").
 *
 * @function splitFilter
 * @param {string} filter Raw filter string, e.g. "StudyLevel:140".
 * @returns {ParsedFilter} The parsed filter. `name` is "" if no colon exists.
 */
function splitFilter(filter) {
    const index = filter.indexOf(':');

    if (index === -1) {
        return { name: '', value: filter };
    }

    return {
        name: filter.slice(0, index),
        value: filter.slice(index + 1)
    };
}

/**
 * Highlights facet options that match the active URL filters.
 *
 * - Adds `.active` to every matching `.facet-option`.
 * - Adds `.has-active` to the `.dropdown-linklist` wrapper if at least one
 *   of its options is active (useful for styling the dropdown button).
 * - Removes both classes where they no longer apply, so stale highlighting
 *   disappears when a filter is removed.
 *
 * Matching is done on the facet value only. If two facets can share values,
 * add e.g. `data-facet-name` to the options and also compare `filter.name`.
 *
 * @function highlightActiveFacets
 * @param {string[]} activeFilters Raw filter strings from getActiveFiltersFromUrl().
 * @returns {void}
 */
function highlightActiveFacets(activeFilters) {
    // Parse once, outside the loops.
    const parsed = activeFilters.map(splitFilter);

    document.querySelectorAll('.dropdown-linklist').forEach((wrapper) => {
        let hasActive = false;

        wrapper.querySelectorAll('.facet-option').forEach((item) => {
            const facetValue = normalize(item.dataset.facetItemValue);

            const isActive = parsed.some(
                (filter) => normalize(filter.value) === facetValue
            );

            // toggle(name, force) adds or removes depending on `force`.
            item.classList.toggle('active', isActive);

            if (isActive) {
                hasActive = true;
            }
        });

        wrapper.classList.toggle('has-active', hasActive);
    });
}

/**
 * Shows a badge with the number of selected options on each dropdown button.
 *
 * Any existing badge is removed first, so the function can be called
 * repeatedly without creating duplicates. The count is based on the
 * `.active` classes set by highlightActiveFacets(), so that function must
 * run first.
 *
 * @function updateDropdownButtonText
 * @returns {void}
 */
function updateDropdownButtonText() {
    document.querySelectorAll('.dropdown-linklist').forEach((wrapper) => {
        const buttonText = wrapper.querySelector('.btn-text');
        if (!buttonText) {
            return;
        }

        // Remove old badge(s) to avoid duplicates.
        wrapper.querySelectorAll('.badge').forEach((badge) => badge.remove());

        const count = wrapper.querySelectorAll('.facet-option.active').length;

        if (count > 0) {
            const badge = document.createElement('span');
            badge.className = 'badge';
            badge.textContent = String(count);

            // Place the badge directly after the label element.
            buttonText.insertAdjacentElement('afterend', badge);
        }
    });
}

/**
 * Scrolls to the Solr result list after filters have been applied.
 *
 * Only scrolls if:
 * - The URL contains `tx_solr` (i.e. a search/filter is active)
 * - The result list element exists
 * - Less than 400px of the list is currently visible
 *
 * The list is centered vertically in the viewport. Smooth scrolling is
 * disabled for users who prefer reduced motion.
 *
 * @function scrollToResults
 * @returns {void}
 */
function scrollToResults() {
    if (!window.location.search.includes('tx_solr')) {
        return;
    }

    const list = document.querySelector('[id*="tx-solr-search-"]');
    if (!list) {
        return;
    }

    const rect = list.getBoundingClientRect();

    // Height of the list that is currently inside the viewport.
    const visibleHeight = Math.max(
        0,
        Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0)
    );

    // Already mostly visible: do nothing.
    if (visibleHeight >= 400) {
        return;
    }

    // Absolute Y position of the list, minus half a viewport to center it.
    const targetY = window.scrollY + rect.top - (window.innerHeight / 2);

    window.scrollTo({
        top: Math.max(0, targetY),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth'
    });
}